import * as React from "react";

import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";

const cardVariants = cva(
  "bg-card text-card-foreground flex flex-col gap-6 rounded-xl border border-(--color-border) shadow-sm",
  {
    variants: {
      variant: {
        default: "",
        "marketing-glass":
          "rounded-(--radius-2xl) border-(--card-border-glass) bg-card-surface-glass text-(--color-text-primary) shadow-card-xl backdrop-blur-(--blur-md) supports-[backdrop-filter]:backdrop-blur-(--blur-md)",
        "marketing-veil":
          "rounded-(--radius-2xl) border-(--card-border-glass) bg-card-surface-veil text-(--color-text-primary) shadow-card-md backdrop-blur-(--blur-md) supports-[backdrop-filter]:backdrop-blur-(--blur-md)",
        // Opaque white sibling of `marketing-veil`, for long-form reading surfaces where artwork
        // drawn on white has to sit flush against the page without a visible tint seam.
        "marketing-solid":
          "rounded-(--radius-2xl) border-(--card-border-glass) bg-basic-white text-(--color-text-primary) shadow-card-md",
      },
      padding: {
        none: "",
        md: "px-6 py-6",
        lg: "px-8 py-8",
        marketing: "p-6 md:p-10",
        // Long-form reading surface: the same inline padding as `marketing`, with more room above
        // and below so the text block breathes against the page edges.
        article: "px-6 py-12 md:px-10 md:py-20",
      },
    },
    defaultVariants: {
      variant: "default",
      padding: "md",
    },
  },
);

type CardProps = React.ComponentProps<"div"> & VariantProps<typeof cardVariants> & { asChild?: boolean };

function Card({ className, variant, padding, asChild = false, ...props }: CardProps) {
  const Comp = asChild ? Slot : "div";

  return <Comp data-slot="card" className={cn(cardVariants({ variant, padding, className }))} {...props} />;
}

function CardHeader({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="card-header"
      className={cn(
        "@container/card-header grid auto-rows-min grid-rows-[auto_auto] items-start gap-2 px-6 has-data-[slot=card-action]:grid-cols-[1fr_auto] [.border-b]:pb-6",
        className,
      )}
      {...props}
    />
  );
}

function CardTitle({ className, ...props }: React.ComponentProps<"div">) {
  return <div data-slot="card-title" className={cn("leading-none font-semibold", className)} {...props} />;
}

function CardDescription({ className, ...props }: React.ComponentProps<"div">) {
  return <div data-slot="card-description" className={cn("text-muted-foreground text-sm", className)} {...props} />;
}

function CardAction({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="card-action"
      className={cn("col-start-2 row-span-2 row-start-1 self-start justify-self-end", className)}
      {...props}
    />
  );
}

function CardContent({ className, ...props }: React.ComponentProps<"div">) {
  return <div data-slot="card-content" className={cn("px-6", className)} {...props} />;
}

function CardFooter({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div data-slot="card-footer" className={cn("flex items-center px-6 [.border-t]:pt-6", className)} {...props} />
  );
}

export { Card, CardHeader, CardFooter, CardTitle, CardAction, CardDescription, CardContent };
